import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Alert
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import API_CONFIG from '../../config/api';

const SimpleSliderCaptcha = ({ 
  onVerify, 
  onReset,
  width = 300, 
  height = 125
}) => {
  const canvasRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(10);
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [captchaData, setCaptchaData] = useState(null);
  const [startX, setStartX] = useState(0);

  // 获取验证码数据
  const fetchCaptcha = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      
      console.log('正在获取验证码...', API_CONFIG.BASE_URL + API_CONFIG.CAPTCHA.GENERATE);
      
      const response = await fetch(API_CONFIG.BASE_URL + API_CONFIG.CAPTCHA.GENERATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('验证码响应状态:', response.status);
      
      if (!response.ok) {
        throw new Error('获取验证码失败');
      }
      
      const data = await response.json();
      console.log('验证码数据:', data);
      setCaptchaData(data);
      
      // 绘制验证码
      drawCaptcha(data);
      
    } catch (err) {
      setError('获取验证码失败，请重试');
      console.error('Captcha fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 绘制验证码
  const drawCaptcha = (data) => {
    const canvas = canvasRef.current;
    if (!canvas || !data) return;

    const ctx = canvas.getContext('2d');
    
    // 获取设备像素比，确保高清显示
    const devicePixelRatio = window.devicePixelRatio || 1;
    
    // 设置画布实际尺寸（物理像素）
    canvas.width = width * devicePixelRatio;
    canvas.height = height * devicePixelRatio;
    
    // 设置画布显示尺寸（CSS像素）
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    canvas.style.display = 'block';
    
    // 缩放上下文以匹配设备像素比
    ctx.scale(devicePixelRatio, devicePixelRatio);
    
    // 先绘制一个默认背景
    ctx.fillStyle = '#f0f8ff';
    ctx.fillRect(0, 0, width, height);
    
    // 绘制背景图片
    const bgImg = new Image();
    bgImg.crossOrigin = 'anonymous';
    bgImg.onload = () => {
      console.log('背景图片加载成功', bgImg.width, bgImg.height);
      ctx.drawImage(bgImg, 0, 0, width, height);
      
      // 绘制缺口
      if (data.gapX !== undefined) {
        drawGap(ctx, data.gapX, data.gapY || height/2 - 20, data.gapWidth || 40, data.gapHeight || 40);
      }
      
      // 绘制初始滑块
      drawSlider(10);
    };
    bgImg.onerror = (error) => {
      console.error('背景图片加载失败:', error);
      setError('验证码图片加载失败，请重试');
    };
    bgImg.src = data.backgroundImage;
  };

  // 绘制缺口
  const drawGap = (ctx, x, y, width, height) => {
    ctx.save();
    
    // 绘制白色缺口背景
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x, y, width, height);
    
    // 绘制缺口边框（浅灰色，便于识别）
    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, width, height);
    
    ctx.restore();
  };

  // 绘制滑块
  const drawSlider = (position) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    console.log('绘制滑块，位置:', position);
    
    // 不需要清除区域，因为滑块是独立的画布
    
    // 绘制滑块
    ctx.save();
    ctx.fillStyle = isVerified ? '#4caf50' : '#1976d2';
    ctx.strokeStyle = isVerified ? '#4caf50' : '#1976d2';
    ctx.lineWidth = 2;
    
    // 绘制滑块主体（使用简单的矩形）
    const sliderX = Math.max(10, Math.min(width - 40, position));
    const sliderY = height - 35;
    const sliderWidth = 30;
    const sliderHeight = 30;
    
    console.log('滑块坐标:', sliderX, sliderY, sliderWidth, sliderHeight);
    
    // 绘制滑块背景
    ctx.fillRect(sliderX, sliderY, sliderWidth, sliderHeight);
    
    // 绘制滑块边框
    ctx.strokeRect(sliderX, sliderY, sliderWidth, sliderHeight);
    
    // 绘制滑块图标
    ctx.fillStyle = 'white';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(isVerified ? '✓' : '→', sliderX + sliderWidth/2, sliderY + sliderHeight/2 + 5);
    
    ctx.restore();
  };

  // 验证滑块位置
  const verifyPosition = async (position) => {
    if (!captchaData) return false;

    try {
      setIsLoading(true);
      setError('');

      const response = await fetch(API_CONFIG.BASE_URL + API_CONFIG.CAPTCHA.VERIFY, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          captchaId: captchaData.id,
          position: position
        }),
      });

      if (!response.ok) {
        throw new Error('验证失败');
      }

      const result = await response.json();
      
      if (result.success) {
        setIsVerified(true);
        onVerify && onVerify();
        return true;
      } else {
        setError('验证失败，请重试');
        // 验证失败后重置
        setTimeout(() => {
          handleReset();
        }, 1000);
        return false;
      }
    } catch (err) {
      setError('验证失败，请重试');
      console.error('Captcha verify error:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // 鼠标按下事件
  const handleMouseDown = (e) => {
    if (isVerified || isLoading) return;
    
    setIsDragging(true);
    setStartX(e.clientX - sliderPosition);
    e.preventDefault();
  };

  // 鼠标移动事件
  const handleMouseMove = (e) => {
    if (!isDragging || isVerified || isLoading) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const relativeX = e.clientX - rect.left;
    const newPosition = Math.max(10, Math.min(width - 40, relativeX));
    
    setSliderPosition(newPosition);
    drawSlider(newPosition);
  };

  // 鼠标抬起事件
  const handleMouseUp = (e) => {
    if (!isDragging || isVerified || isLoading) return;
    
    setIsDragging(false);
    
    // 验证位置
    verifyPosition(sliderPosition);
  };

  // 触摸事件处理
  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousedown', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    handleMouseDown(mouseEvent);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const mouseEvent = new MouseEvent('mousemove', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    handleMouseMove(mouseEvent);
  };

  const handleTouchEnd = (e) => {
    const touch = e.changedTouches[0];
    const mouseEvent = new MouseEvent('mouseup', {
      clientX: touch.clientX,
      clientY: touch.clientY
    });
    handleMouseUp(mouseEvent);
  };

  // 重置验证码
  const handleReset = () => {
    setSliderPosition(10);
    setIsVerified(false);
    setIsDragging(false);
    setError('');
    onReset && onReset();
    fetchCaptcha();
  };

  // 组件挂载时获取验证码
  useEffect(() => {
    fetchCaptcha();
  }, [fetchCaptcha]);

  return (
    <Box sx={{ width: width + 20, mx: 'auto' }}>
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2, 
          borderRadius: 2,
          backgroundColor: '#fafafa'
        }}
      >
        <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
          请拖动滑块完成拼图验证
        </Typography>
        
        {/* 验证码画布 */}
        <Box sx={{ position: 'relative', mb: 2 }}>
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            style={{
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              display: 'block',
              margin: '0 auto',
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
              imageRendering: 'crisp-edges',
              imageRendering: '-webkit-optimize-contrast'
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
          
          {/* 验证成功提示 */}
          {isVerified && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(76, 175, 80, 0.9)',
                color: 'white',
                borderRadius: '50%',
                width: 50,
                height: 50,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px'
              }}
            >
              <CheckIcon />
            </Box>
          )}
        </Box>

        {/* 错误提示 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 控制按钮 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <IconButton
            onClick={handleReset}
            disabled={isLoading}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <RefreshIcon />
          </IconButton>
          
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {isVerified ? '验证成功' : isLoading ? '验证中...' : '拖动滑块完成验证'}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default SimpleSliderCaptcha;
